import { Shield, Lock, Database, Users, Trash2, Ban, KeyRound, type LucideIcon } from "lucide-react";
import { useI18n } from "@/lib/i18n";

export function TrustSecurity() {
  const { t } = useI18n();
  const cards: Array<{ icon: LucideIcon; titleKey: string; descKey: string }> = [
    { icon: KeyRound, titleKey: "trust.c1.title", descKey: "trust.c1.desc" },
    { icon: Database, titleKey: "trust.c2.title", descKey: "trust.c2.desc" },
    { icon: Lock, titleKey: "trust.c3.title", descKey: "trust.c3.desc" },
    { icon: Users, titleKey: "trust.c4.title", descKey: "trust.c4.desc" },
    { icon: Trash2, titleKey: "trust.c5.title", descKey: "trust.c5.desc" },
    { icon: Ban, titleKey: "trust.c6.title", descKey: "trust.c6.desc" },
  ];
  const trustItems = [
    t("trust.bar.1"),
    t("trust.bar.2"),
    t("trust.bar.3"),
    t("trust.bar.4"),
    t("trust.bar.5"),
  ];
  return (
    <section className="relative overflow-hidden bg-background py-24">
      <div className="pointer-events-none absolute inset-0 flex items-center justify-center opacity-[0.03]">
        <Shield className="h-[600px] w-[600px]" strokeWidth={1} />
      </div>
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-background via-background/80 to-background" />

      <div className="container relative mx-auto px-4">
        <div className="mx-auto max-w-3xl text-center">
          <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-border bg-card/50 px-4 py-1.5 text-sm text-muted-foreground backdrop-blur">
            <Lock className="h-4 w-4 text-primary" />
            {t("trust.badge")}
          </div>
          <h2 className="text-balance text-4xl font-bold tracking-tight md:text-5xl">
            {t("trust.title.1")}{" "}
            <span className="bg-gradient-to-r from-primary to-primary/60 bg-clip-text text-transparent">
              {t("trust.title.2")}
            </span>
          </h2>
          <p className="mt-4 text-lg text-muted-foreground">
            {t("trust.subtitle")}
          </p>
        </div>

        <div className="mx-auto mt-16 grid max-w-6xl grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3">
          {cards.map(({ icon: Icon, titleKey, descKey }) => (
            <div
              key={titleKey}
              className="relative p-7 rounded-3xl bg-card/60 border border-border text-center overflow-hidden"
            >
              <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-secondary/5" />
              <div className="relative">
                <div className="w-12 h-12 mx-auto rounded-full bg-primary/15 flex items-center justify-center mb-4">
                  <Icon className="w-6 h-6 text-primary" />
                </div>
                <h3 className="mb-2 text-lg font-bold text-foreground">{t(titleKey)}</h3>
                <p className="text-sm leading-relaxed text-muted-foreground">{t(descKey)}</p>
              </div>
            </div>
          ))}
        </div>

        <div className="mx-auto mt-12 max-w-5xl rounded-2xl border border-border bg-card/40 px-6 py-5 backdrop-blur">
          <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-3 text-sm text-muted-foreground">
            {trustItems.map((item, i) => (
              <div key={item} className="flex items-center gap-2">
                {i === 0 && <Database className="h-4 w-4 text-primary" />}
                {i === 1 && <Lock className="h-4 w-4 text-primary" />}
                {i === 2 && <KeyRound className="h-4 w-4 text-primary" />}
                {i === 3 && <Users className="h-4 w-4 text-primary" />}
                {i === 4 && <Shield className="h-4 w-4 text-primary" />}
                <span className="font-medium">{item}</span>
                {i < trustItems.length - 1 && (
                  <span className="ml-6 hidden text-border md:inline">•</span>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
